import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CreateNewProduct.module.css';
import masaLogo from '../Assets/MasaLogo.png';
import { createProduct } from '../api/productApi';

const CreateNewProduct = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [size, setSize] = useState('');
  const [SKU, setSKU] = useState('');
  const [sorts, setSorts] = useState([{ sortName: '', colors: [{ color: '', quantity: 0 }] }]);
  const [imageFile, setImageFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleAddSort = () => {
    setSorts([...sorts, { sortName: '', colors: [{ color: '', quantity: 0 }] }]);
  };

  const handleRemoveSort = (index) => {
    const updatedSorts = [...sorts];
    updatedSorts.splice(index, 1);
    setSorts(updatedSorts);
  };

  const handleSortChange = (index, field, value) => {
    const updatedSorts = [...sorts];
    updatedSorts[index][field] = value;
    setSorts(updatedSorts);
  };

  const handleAddColorToSort = (sortIndex) => {
    const updatedSorts = [...sorts];
    updatedSorts[sortIndex].colors.push({ color: '', quantity: 0 });
    setSorts(updatedSorts);
  };

  const handleRemoveColorFromSort = (sortIndex, colorIndex) => {
    const updatedSorts = [...sorts];
    updatedSorts[sortIndex].colors.splice(colorIndex, 1);
    setSorts(updatedSorts);
  };

  const handleColorChange = (sortIndex, colorIndex, field, value) => {
    const updatedSorts = [...sorts];
    updatedSorts[sortIndex].colors[colorIndex][field] =
      field === 'quantity' ? parseInt(value, 10) || 0 : value;
    setSorts(updatedSorts);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPEG, JPG, PNG, and GIF files are allowed.');
        e.target.value = null;
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5 MB.');
        e.target.value = null;
        return;
      }
      setImageFile(file);
    } else {
      setImageFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', name);
    formData.append('size', size);
    formData.append('SKU', SKU);
    formData.append('sorts', JSON.stringify(sorts));
    if (imageFile) {
      formData.append('image', imageFile);
    }

    try {
      await createProduct(formData);
      navigate('/product-management');
    } catch (error) {
      console.error('Error creating product:', error);
      if (error.response && error.response.data && error.response.data.error) {
        setErrorMessage(error.response.data.error);
      } else {
        setErrorMessage('Failed to create product. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    navigate('/factory-manager-dashboard');
  };

  const handleCloseModal = () => {
    setErrorMessage(null);
  };

  return (
    <main className={styles.createProductView}>
      <header className={styles.header}>
        <img src={masaLogo} className={styles.logo} alt="Dashboard Logo" />
        <h1 className={styles.pageTitle}>Create New Product</h1>
      </header>

      <div className={styles.formContainer}>
        <form className={styles.productForm} onSubmit={handleSubmit}>
          <h2 className={styles.formHeading}>Product Details</h2>

          <label className={styles.formLabel}>Name:</label>
          <input
            className={styles.formInput}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Enter product name"
          />

          <label className={styles.formLabel}>Size:</label>
          <input
            className={styles.formInput}
            type="text"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            required
            placeholder="Enter product size"
          />

          <label className={styles.formLabel}>SKU:</label>
          <input
            className={styles.formInput}
            type="text"
            value={SKU}
            onChange={(e) => setSKU(e.target.value)}
            required
            placeholder="Enter unique SKU"
          />

          <label className={styles.formLabel}>Product Image (Optional):</label>
          <input
            type="file"
            accept="image/*"
            className={styles.imageInput}
            onChange={handleImageChange}
          />

          <h2 className={styles.formHeading}>Sorts</h2>
          {sorts.map((sort, sortIndex) => (
            <div key={sortIndex} className={styles.sortContainer}>
              <label className={styles.formLabel}>Sort Name:</label>
              <input
                className={styles.sortInput}
                type="text"
                value={sort.sortName}
                onChange={(e) => handleSortChange(sortIndex, 'sortName', e.target.value)}
                required
                placeholder="Enter sort name"
              />
              <h3 className={styles.colorHeading}>Colors &amp; Quantities</h3>
              {sort.colors.map((colorObj, colorIndex) => (
                <div key={colorIndex} className={styles.colorRow}>
                  <input
                    className={styles.colorInput}
                    type="text"
                    value={colorObj.color}
                    onChange={(e) => handleColorChange(sortIndex, colorIndex, 'color', e.target.value)}
                    placeholder="Color name"
                    required
                  />
                  <input
                    className={styles.quantityInput}
                    type="number"
                    min="0"
                    value={colorObj.quantity}
                    onChange={(e) => handleColorChange(sortIndex, colorIndex, 'quantity', e.target.value)}
                    placeholder="Quantity"
                    required
                  />
                  {sort.colors.length > 1 && (
                    <button
                      type="button"
                      className={styles.removeColorButton}
                      onClick={() => handleRemoveColorFromSort(sortIndex, colorIndex)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className={styles.addColorButton}
                onClick={() => handleAddColorToSort(sortIndex)}
              >
                + Add Another Color
              </button>
              {sorts.length > 1 && (
                <button
                  type="button"
                  className={styles.removeSortButton}
                  onClick={() => handleRemoveSort(sortIndex)}
                >
                  Remove Sort
                </button>
              )}
            </div>
          ))}
          <button type="button" className={styles.addSortButton} onClick={handleAddSort}>
            + Add Another Sort
          </button>

          <div className={styles.buttonGroup}>
            <button type="submit" className={styles.submitButton}>Create Product</button>
            <button type="button" className={styles.cancelButton} onClick={handleCancel}>Cancel</button>
          </div>
        </form>
      </div>

      {errorMessage && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>Warning</h2>
            <p>{errorMessage}</p>
            <button onClick={handleCloseModal} className={styles.modalCloseButton}>Close</button>
          </div>
        </div>
      )}
    </main>
  );
};

export default CreateNewProduct;
